import { useState } from 'react';
import { Editor } from '@tiptap/react';
import { Sparkles, X } from 'lucide-react';

interface AiReplyAssistModalProps {
  editor: Editor | null;
  onClose: () => void;
  leadName?: string;
  lastMessage?: string;
}

const AZURE_ENDPOINT = import.meta.env.VITE_AZURE_ENDPOINT;
const AZURE_DEPLOYMENT = import.meta.env.VITE_AZURE_DEPLOYMENT;
const AZURE_API_VERSION = import.meta.env.VITE_AZURE_API_VERSION;
const AZURE_API_KEY = import.meta.env.VITE_AZURE_API_KEY;

export default function AiReplyAssistModal({ editor, onClose, leadName, lastMessage }: AiReplyAssistModalProps) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please describe the reply you want to generate.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const url = `${AZURE_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT}/chat/completions?api-version=${AZURE_API_VERSION}`;
      const contextParts: string[] = [];
      if (leadName) contextParts.push(`You are replying to ${leadName}.`);
      if (lastMessage) contextParts.push(`Their last message was: "${lastMessage}"`);

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_API_KEY,
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You are an expert B2B sales professional writing email replies. Generate professional, concise, and personalized reply emails. ${contextParts.join(' ')} Return only the reply body text (no subject line, no signature block). Use plain paragraphs — no markdown headers or bullets unless specifically requested. Keep it under 150 words unless the user asks for more.`,
            },
            { role: 'user', content: prompt },
          ],
          temperature: 0.7,
          max_completion_tokens: 400,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error((errData as { error?: { message?: string } })?.error?.message ?? `Request failed (${res.status})`);
      }

      const data = await res.json();
      const text: string = (data as { choices?: { message?: { content?: string } }[] })?.choices?.[0]?.message?.content ?? '';
      if (!text.trim()) throw new Error('Empty response from AI.');

      if (editor) {
        editor.chain().focus().setContent(`<p>${text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`).run();
      }
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-modal-overlay">
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <div className="ai-modal-title-row">
            <Sparkles size={16} className="ai-modal-icon" />
            <span className="ai-modal-title">AI Reply Writer</span>
          </div>
          <button className="ai-modal-close" onClick={onClose} type="button">
            <X size={16} />
          </button>
        </div>

        <p className="ai-modal-hint">
          Describe how you want to reply and AI will generate a professional response for you.
        </p>

        <textarea
          className="ai-modal-textarea"
          placeholder="e.g. Accept their request for a demo next week, suggest Tuesday at 2pm, keep it brief and friendly..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          disabled={loading}
        />

        {error && <p className="ai-modal-error">{error}</p>}

        <div className="ai-modal-footer">
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm ai-modal-generate-btn"
            type="button"
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
          >
            {loading ? (
              <>
                <span className="ai-modal-spinner" /> Generating…
              </>
            ) : (
              <>
                <Sparkles size={13} /> Generate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
